import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faPlay, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { Button, Table, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/rootReducer';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { clearData } from '../../../redux/modules/contacts/contactSlice';
import moment from 'moment';
import { API_ENDPOINTS } from '../../../constants/api';
import useAxios from '../../../hooks/useAxios';
import { toast } from 'react-toastify';
import useSidebar from '../../../hooks/useSidebar';
import PaginationNav from '../../../component/pagination/pagination';
import XButton from '../../../component/button/XButton';

const PAGE_SIZE = 50;

const ReviewSegment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const draftValues = location.state?.draftValues;
  const { postData, putData } = useAxios();
  const { segmentId } = useParams();
  const dispatch = useDispatch();
  const { createSegment, editedSegment, loading } = useSelector((state: RootState) => state.contactlist);
  const { orgLocations, selectedLocationId } = useSelector((state: RootState) => state.user);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isUsingSegment, setIsUsingSegment] = useState(false);
  const [segmentProgress, setSegmentProgress] = useState(0);

  const activeLocation = (orgLocations ?? []).find(
    (loc: any) => String(loc.id) === String(selectedLocationId)
  ) || (orgLocations && orgLocations.length > 0 ? orgLocations[0] : null);

  const locationTimezone = activeLocation?.location_timezone || 'UTC';
  const { onShowSidebar } = useSidebar();

  const segmentData = segmentId ? editedSegment : createSegment;
  const rawContacts = useMemo(() => {
    return segmentData?.contactCount?.contacts || [];
  }, [segmentData]);

  const filteredContacts = useMemo(() => {
    if (!searchTerm.trim()) {
      return rawContacts;
    }
    const term = searchTerm.toLowerCase().trim();
    return rawContacts.filter((contact: any) =>
      contact?.email?.toLowerCase().includes(term) ||
      contact?.firstname?.toLowerCase().includes(term) ||
      contact?.lastname?.toLowerCase().includes(term) ||
      contact?.phone_number?.toLowerCase().includes(term) ||
      contact?.address?.toLowerCase().includes(term)
    );
  }, [rawContacts, searchTerm]);

  const totalContacts = filteredContacts.length;
  const totalPages = Math.max(1, Math.ceil(totalContacts / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const paginatedContacts = useMemo(() => {
    const startIndex = (safePage - 1) * PAGE_SIZE;
    return filteredContacts.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredContacts, safePage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const startRecord = totalContacts === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const endRecord = Math.min(safePage * PAGE_SIZE, totalContacts);

  const handleUseSegment = async () => {
    try {
      setIsUsingSegment(true);
      setSegmentProgress(10);
      const interval = setInterval(() => {
        setSegmentProgress((prev) => (prev >= 95 ? 95 : prev + Math.max(1, Math.floor((96 - prev) * 0.15))));
      }, 200);

      const payload = {
        name: segmentId ? editedSegment?.segmentName : createSegment?.segmentName,
        filters: segmentId ? editedSegment?.filters : createSegment?.filters,
      };
      if (segmentId) {
        const response = await putData(`${API_ENDPOINTS.UPDATE_USE_SEGMENT}/${segmentId}`, payload);
        clearInterval(interval);
        if (response.success === true) {
          setSegmentProgress(100);
          toast(response.message);
          setTimeout(() => {
            navigate('/org/contacts/segments');
          }, 400);
        } else {
          setIsUsingSegment(false);
          setSegmentProgress(0);
        }
      } else {
        const response = await postData(API_ENDPOINTS.USE_SEGMENT, payload);
        clearInterval(interval);
        if (response.success === true) {
          setSegmentProgress(100);
          toast(response.message);
          setTimeout(() => {
            navigate('/org/contacts/segments');
          }, 400);
        } else {
          setIsUsingSegment(false);
          setSegmentProgress(0);
        }
      }
    } catch (error) {
      setIsUsingSegment(false);
      setSegmentProgress(0);
    }
  };

  const handleBackToEditing = () => {
    if (segmentId) {
      navigate(`/org/edit-segment/${segmentId}`);
    } else {
      navigate('/org/contacts/createSegment', {
        state: {
          draftValues: {
            segmentName: draftValues?.segmentName || createSegment?.segmentName || '',
            filters: draftValues?.filters || createSegment?.filters || [],
          },
        },
      });
    }
    dispatch(clearData());
  };

  const currentDate = (() => {
    try {
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: locationTimezone,
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      const parts = dtf.formatToParts(new Date()).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {} as any);
      return `${parts.month}/${parts.day}/${parts.year} ${parts.hour}:${parts.minute} ${parts.dayPeriod || ''}`.trim();
    } catch (e) {
      return moment().format('MM/DD/YY hh:mm A');
    }
  })();

  return (
    <div>
      <div className="main-title d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <h2 className='back-text mb-0'>
          <span className="d-lg-none cursor-pointer" onClick={() => onShowSidebar()}>
            <FontAwesomeIcon icon={faBars} />
          </span>
          Review Segment
        </h2>
      </div>

      <div className="add-contact table-listing review-contact-segment">
        <div className="review-segment mb-3">
          <h2 className='segment-name mb-2'>{segmentData?.segmentName || ''}</h2>
          <div className="contact-count">
            <p className='d-flex align-items-center mb-0'>
              <span className='count'>{segmentData?.contactCount?.contactCount ?? rawContacts.length}</span>
              <span className='text'>contacts in your segment</span>
              <span className='date-time'>as of {currentDate}</span>
            </p>
          </div>
        </div>

        {/* Search bar & count summary */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div className="position-relative" style={{ minWidth: '280px', maxWidth: '400px' }}>
            <Form.Control
              type="text"
              placeholder="Search in segment contacts..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#13294B',
                pointerEvents: 'none',
              }}
            />
          </div>
          <div className="text-muted" style={{ fontSize: '14px' }}>
            <span>
              {searchTerm
                ? `Found ${totalContacts.toLocaleString()} matching contact${totalContacts === 1 ? '' : 's'}`
                : `Showing ${startRecord.toLocaleString()} - ${endRecord.toLocaleString()} of ${totalContacts.toLocaleString()} contacts`}
            </span>
            {totalPages > 1 && (
              <span className="ms-2 fw-semibold">
                (Page {safePage} of {totalPages})
              </span>
            )}
          </div>
        </div>

        <Table responsive>
          <thead>
            <tr>
              <th>Email Address</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Address </th>
              <th>Phone Number </th>
            </tr>
          </thead>
          <tbody>
            {(!loading && paginatedContacts.length === 0) ? (
              <tr>
                <td colSpan={5}>
                  <p className='text-center no-records mb-0 py-4'>
                    {searchTerm ? `No contacts found matching "${searchTerm}"` : 'No Contacts found.'}
                  </p>
                </td>
              </tr>
            ) : (
              paginatedContacts.map((contact: any, idx: number) => (
                <tr key={contact.id || idx}>
                  <td>{contact?.email}</td>
                  <td>{contact?.firstname}</td>
                  <td>{contact?.lastname}</td>
                  <td>{contact?.address}</td>
                  <td>{contact?.phone_number}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination & Bottom Action Buttons - outside the grey area */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-4">
        <Button variant="primary" className='btn-theme btn-border' onClick={handleBackToEditing} disabled={isUsingSegment}>
          <FontAwesomeIcon icon={faPlay} /> Back to Editing
        </Button>

        {totalPages > 1 && (
          <div className="modal-pagination-wrapper">
            <PaginationNav
              totalPages={totalPages}
              currentPage={safePage}
              handlePageChange={handlePageChange}
            />
          </div>
        )}

        <XButton
          type="button"
          className='btn-theme'
          buttonText={<>Use Segment <FontAwesomeIcon icon={faPlay} /></>}
          onClick={handleUseSegment}
          loading={isUsingSegment}
          loadingText={`${segmentProgress}% Processing...`}
          disabled={isUsingSegment}
        />
      </div>
    </div>
  );
};

export default ReviewSegment;
